/**
 * ปาร์ตี้ฝั่ง client — คำนวณว่าจะแสดงใครบ้าง และแปลงคำสั่งแชทเป็นคำขอ
 *
 * **ไม่ตัดสินอะไรเอง** ใครอยู่ปาร์ตี้ไหน ใครเป็นหัวหน้า มาจาก `partyId`
 * กับ `partyLeader` ที่ server sync มาเท่านั้น (กติกาอยู่ที่ server/src/party.ts)
 * ไฟล์นี้แค่จัดเรียงเพื่อวาด และแปลงข้อความที่ผู้เล่นพิมพ์เป็นคำขอที่ส่งให้ server
 *
 * pure function ทั้งหมด ไม่รู้จัก DOM หรือ Colyseus จึงเทสต์ด้วยข้อมูลเปล่า ๆ ได้
 */

/** ข้อมูลผู้เล่นเท่าที่หน้าปาร์ตี้ต้องใช้ (ตรงกับฟิลด์ใน PlayerState) */
export interface PartyPlayer {
  id: string;
  name: string;
  cls: string;
  baseLevel: number;
  hp: number;
  maxHp: number;
  dead: boolean;
  partyId: string;
  partyLeader: boolean;
}

/**
 * สมาชิกปาร์ตี้ของเรา เรียง หัวหน้าก่อน แล้วตามชื่อ
 *
 * เรียงให้คงที่ ไม่งั้นแถบเลือดสลับที่ไปมาทุกครั้งที่ state เปลี่ยน
 * แล้วผู้เล่นจับไม่ได้ว่าแถบไหนเป็นของใคร
 *
 * @param includeSelf แถบเพื่อนบน HUD ไม่ต้องมีตัวเอง (มีกรอบของตัวเองอยู่แล้ว)
 *                    แต่หน้าต่างปาร์ตี้ต้องมี จะได้เห็นว่าทั้งทีมมีใครบ้าง
 */
export function partyMembers(
  players: Iterable<PartyPlayer>,
  selfId: string,
  includeSelf: boolean,
): PartyPlayer[] {
  const list = [...players];
  const me = list.find((p) => p.id === selfId);
  if (!me || !me.partyId) return [];
  return list
    .filter((p) => p.partyId === me.partyId && (includeSelf || p.id !== selfId))
    .sort((a, b) =>
      Number(b.partyLeader) - Number(a.partyLeader) || a.name.localeCompare(b.name, "th"));
}

/** ผู้เล่นในแผนที่ที่ยังไม่มีปาร์ตี้ (ชวนได้) ไม่รวมตัวเอง เรียงตามชื่อ */
export function invitable(players: Iterable<PartyPlayer>, selfId: string): PartyPlayer[] {
  return [...players]
    .filter((p) => p.id !== selfId && !p.partyId)
    .sort((a, b) => a.name.localeCompare(b.name, "th"));
}

/** สัดส่วน HP 0..1 กันหารศูนย์และค่าเพี้ยน */
export function hpRatio(p: Pick<PartyPlayer, "hp" | "maxHp" | "dead">): number {
  if (p.dead || !(p.maxHp > 0)) return 0;
  return Math.min(1, Math.max(0, p.hp / p.maxHp));
}

/** คำขอที่ได้จากคำสั่งแชท ส่งให้ server ตามชื่อ `type` */
export type PartyCommand =
  | { type: "partyInvite"; name: string }
  | { type: "partyKick"; name: string }
  | { type: "partyLeave" }
  | { type: "partyChat"; text: string }
  | { type: "help" };

/**
 * แปลงข้อความแชทที่ขึ้นต้นด้วยคำสั่งปาร์ตี้ คืน undefined ถ้าไม่ใช่คำสั่งปาร์ตี้
 *
 * - `/invite ชื่อ` หรือ `/i ชื่อ` — ชวน
 * - `/kick ชื่อ` — เตะ (หัวหน้าเท่านั้น server ตรวจเอง)
 * - `/leave` — ออก
 * - `/p ข้อความ` — แชทปาร์ตี้
 * - `/party` เปล่า ๆ — บอกวิธีใช้
 *
 * **ชื่อมีเว้นวรรคได้** เอาทั้งหมดหลังคำสั่ง ไม่ใช่แค่คำแรก
 * คำสั่งที่ขาดชื่อหรือข้อความคืน `help` แทนการส่งคำขอว่างไปให้ server
 */
export function parsePartyCommand(raw: string): PartyCommand | undefined {
  const text = raw.trim();
  const m = /^\/(\S+)\s*(.*)$/u.exec(text);
  if (!m) return undefined;
  const cmd = m[1]!.toLowerCase();
  const arg = m[2]!.trim();

  switch (cmd) {
    case "invite":
    case "i":
      return arg ? { type: "partyInvite", name: arg } : { type: "help" };
    case "kick":
      return arg ? { type: "partyKick", name: arg } : { type: "help" };
    case "leave":
      return { type: "partyLeave" };
    case "p":
      return arg ? { type: "partyChat", text: arg } : { type: "help" };
    case "party":
      return { type: "help" };
    default:
      return undefined;
  }
}

export const PARTY_HELP =
  "คำสั่งปาร์ตี้: /invite ชื่อ · /kick ชื่อ · /leave · /p ข้อความ (หรือเปิดหน้าต่างปาร์ตี้)";

/** แถวในรายชื่อปาร์ตี้ที่ server ส่งมา (event `partyRoster`) ตรงกับ RosterEntry ฝั่ง server */
export interface RosterMember {
  key: string;
  name: string;
  cls: string;
  lv: number;
  zone: string;
  leader: boolean;
  away: boolean;
}

/**
 * สมาชิกที่ไม่ได้อยู่ห้องเดียวกับเรา (อยู่โซนอื่น หรือกำลังย้ายโซน)
 *
 * คนที่อยู่โซนเดียวกันวาดจาก state ของห้องอยู่แล้ว (มี HP สด) จึงไม่เอามาซ้ำ
 * เรียงเหมือน `partyMembers` หัวหน้าก่อนแล้วตามชื่อ
 */
export function remoteMembers(roster: readonly RosterMember[], myZone: string): RosterMember[] {
  return roster
    .filter((m) => m.away || m.zone !== myZone)
    .sort((a, b) => Number(b.leader) - Number(a.leader) || a.name.localeCompare(b.name, "th"));
}
